import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box } from '@mui/material';
import { motion, useInView } from 'framer-motion';

const AnimatedCounter = ({ 
  end, 
  duration = 2, 
  suffix = '', 
  prefix = '', 
  decimal = 0,
  start = 0,
  delay = 0,
  color = 'primary.main'
}) => {
  const [count, setCount] = useState(start);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    if (isInView && !hasStarted) {
      setHasStarted(true);
      
      const startTime = Date.now();
      const endTime = startTime + duration * 1000;
      
      const timer = setTimeout(() => {
        const updateCounter = () => {
          const now = Date.now();
          const progress = Math.min((now - startTime) / (endTime - startTime), 1);
          
          // Easing function for smooth animation
          const easeOutQuart = 1 - Math.pow(1 - progress, 4);
          const currentCount = start + (end - start) * easeOutQuart;
          
          setCount(currentCount);
          
          if (progress < 1) {
            requestAnimationFrame(updateCounter);
          } else {
            setCount(end);
          }
        };
        
        requestAnimationFrame(updateCounter);
      }, delay * 1000);

      return () => clearTimeout(timer);
    }
  }, [isInView, hasStarted, start, end, duration, delay]);

  const formatNumber = (num) => {
    if (decimal > 0) {
      return num.toFixed(decimal);
    }
    return Math.floor(num).toString();
  };

  return (
    <Box ref={ref} sx={{ textAlign: 'center' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.5, delay: delay }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: 900,
            color: color,
            fontSize: { xs: '2rem', md: '3rem' },
            lineHeight: 1,
            mb: 1
          }}
        >
          {prefix}{formatNumber(count)}{suffix}
        </Typography>
      </motion.div>
    </Box>
  );
};

export default AnimatedCounter;
